import React, { useState, useMemo } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { ChevronDown, ChevronUp, Target, Users, Building2, MapPin, Save, RefreshCw, X, Plus, Sparkles, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { linkedge } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';

/**
 * Editor for the auto-inferred POST CONTEXT.
 *
 * The platform inspects every profile during the audit and infers WHO the
 * person serves: industry sub-segment, function focus, target audience,
 * customer persona, etc. Two people in the same industry and function can
 * still have completely different target audiences — this editor lets the
 * user fill in the persona details of their target audience before
 * regenerating posts.
 */
function ListEditor({ label, hint, items, onChange, placeholder, testIdBase }) {
  const [draft, setDraft] = useState('');
  const safe = Array.isArray(items) ? items : [];

  const addItem = () => {
    const v = draft.trim();
    if (!v) return;
    if (safe.includes(v)) {
      setDraft('');
      return;
    }
    onChange([...safe, v]);
    setDraft('');
  };

  return (
    <div className="space-y-2">
      <Label className="text-xs font-medium">{label}</Label>
      {hint && (
        <p className="text-[11px] text-muted-foreground -mt-1">{hint}</p>
      )}
      <div className="flex flex-wrap gap-1.5">
        {safe.length === 0 && (
          <span className="text-[11px] text-muted-foreground italic">none yet</span>
        )}
        {safe.map((it, i) => (
          <Badge
            key={`${it}-${i}`}
            variant="secondary"
            className="rounded-md gap-1 pr-1 font-normal"
            data-testid={`${testIdBase}-tag-${i}`}
          >
            {it}
            <button
              type="button"
              onClick={() => onChange(safe.filter((_, j) => j !== i))}
              className="ml-0.5 rounded hover:bg-foreground/10 p-0.5"
              aria-label="Remove"
              data-testid={`${testIdBase}-remove-${i}`}
            >
              <X size={11} />
            </button>
          </Badge>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addItem();
            }
          }}
          placeholder={placeholder}
          className="h-9 text-sm"
          data-testid={`${testIdBase}-input`}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addItem}
          className="h-9 shrink-0"
          data-testid={`${testIdBase}-add`}
        >
          <Plus size={14} className="mr-1" /> Add
        </Button>
      </div>
    </div>
  );
}

const FIELDS = [
  { key: 'industry', label: 'Industry', icon: Building2, type: 'text', placeholder: 'Hospitality' },
  { key: 'industry_sub_segment', label: 'Industry sub-segment', icon: Sparkles, type: 'text', placeholder: 'Luxury hotel groups (South Asia)' },
  { key: 'function', label: 'Function', icon: Target, type: 'text', placeholder: 'Sales' },
  { key: 'designation', label: 'Designation', icon: Target, type: 'text', placeholder: 'Director of Sales' },
  { key: 'company', label: 'Company', icon: Building2, type: 'text', placeholder: 'Marriott International' },
  { key: 'company_type', label: 'Company type', icon: Building2, type: 'text', placeholder: 'B2B SaaS / Vendor / Agency \u2026' },
  { key: 'geography', label: 'Geography', icon: MapPin, type: 'text', placeholder: 'India / South Asia' },
];

const TEXTAREA_FIELDS = [
  { key: 'function_focus', label: 'Function focus — what THIS person owns', placeholder: 'Corporate & MICE group bookings, RFP-led wins, multi-property deals…', rows: 2 },
  { key: 'customer_persona', label: 'Primary buyer / reader (one tight sentence)', placeholder: 'Senior corporate travel manager at an enterprise running annual hotel RFPs…', rows: 2 },
];

const LIST_FIELDS = [
  { key: 'target_audience', label: 'Target audience (personas this profile serves)', hint: "Fill the fields with who your target segment's target audience is.", placeholder: 'e.g. Corporate travel managers', icon: Users },
  { key: 'content_angles', label: 'Content angles (specific topics worth posting on)', placeholder: 'e.g. Multi-city hotel RFP design', icon: Sparkles },
  { key: 'audience_pain_points', label: 'Audience pain points', placeholder: 'e.g. Slow RFP response cycles', icon: Target },
];

export default function PostContextEditor({ analysisId, context, onSaved }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(() => ({ ...(context || {}) }));

  // Sync inbound context when it changes externally (e.g., after regen)
  React.useEffect(() => {
    if (!editing) setDraft({ ...(context || {}) });
  }, [context, editing]);

  const isEdited = Boolean(context?._user_edited_at);

  const summary = useMemo(() => {
    if (!context || Object.keys(context).length === 0) return null;
    const seat = [context.designation, context.company && `at ${context.company}`]
      .filter(Boolean)
      .join(' ');
    return {
      seat: seat || context.function || 'Profile context',
      sub: context.industry_sub_segment || context.industry || '',
      audience:
        Array.isArray(context.target_audience) && context.target_audience.length > 0
          ? context.target_audience.slice(0, 3).join(' · ')
          : context.customer_persona || '',
    };
  }, [context]);

  const handleField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      [...FIELDS, ...TEXTAREA_FIELDS].forEach((f) => {
        const v = draft[f.key];
        if (typeof v === 'string') payload[f.key] = v.trim();
      });
      LIST_FIELDS.forEach((f) => {
        const v = draft[f.key];
        if (Array.isArray(v)) payload[f.key] = v;
      });
      const res = await linkedge.updatePostContext(analysisId, payload);
      toast.success('Post context updated. Click "Regenerate posts" to apply.');
      setEditing(false);
      if (onSaved) onSaved(res.post_context);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to save post context'));
    } finally {
      setSaving(false);
    }
  };

  if (!context && !analysisId) return null;

  return (
    <Card
      className="border-border bg-card/60 backdrop-blur-sm"
      data-testid="post-context-editor"
    >
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="w-full text-left p-4 flex items-start gap-3 hover:bg-foreground/[0.02] transition-colors"
            data-testid="post-context-toggle"
          >
            <span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
              <Target size={15} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-semibold text-foreground">
                  Post Context
                </p>
                <Badge
                  variant="secondary"
                  className="text-[10px] font-mono-edge rounded-md"
                >
                  {isEdited ? 'EDITED' : 'AUTO-INFERRED'}
                </Badge>
                <span className="text-[11px] text-muted-foreground ml-auto inline-flex items-center gap-1">
                  {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  {open ? 'Hide' : 'Review & edit'}
                </span>
              </div>
              {summary && (
                <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                  <span className="text-foreground/85 font-medium">{summary.seat}</span>
                  {summary.sub ? ` · ${summary.sub}` : ''}
                  {summary.audience ? ` → ${summary.audience}` : ''}
                </p>
              )}
              {!summary && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Context will be inferred on next regeneration.
                </p>
              )}
            </div>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-4 pb-5 pt-1 space-y-4 border-t border-border/60">
            <div className="flex items-center justify-between flex-wrap gap-2 pt-3">
              <p className="text-xs text-muted-foreground max-w-2xl">
                These fields shape every research source we pull and every post
                we generate. Fill each field with the <strong>persona details of your target audience</strong> —
                the person you want to influence, sell to, or be seen by. The more
                specific you are (industry, function, seniority, geography), the
                sharper the research and content we can produce. Edit anything
                that looks off, then click <strong>Regenerate posts</strong>.
              </p>
              {!editing ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEditing(true)}
                  data-testid="post-context-edit"
                  className="shrink-0"
                >
                  <Pencil size={14} className="mr-1.5" /> Edit
                </Button>
              ) : (
                <div className="flex gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setDraft({ ...(context || {}) });
                      setEditing(false);
                    }}
                    data-testid="post-context-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={saving}
                    data-testid="post-context-save"
                  >
                    {saving ? (
                      <RefreshCw size={14} className="mr-1.5 animate-spin" />
                    ) : (
                      <Save size={14} className="mr-1.5" />
                    )}
                    Save context
                  </Button>
                </div>
              )}
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {FIELDS.map((f) => (
                <div key={f.key} className="space-y-1.5">
                  <Label className="text-xs font-medium flex items-center gap-1.5">
                    <f.icon size={12} className="text-muted-foreground" />
                    {f.label}
                  </Label>
                  {editing ? (
                    <Input
                      value={draft[f.key] || ''}
                      onChange={(e) => handleField(f.key, e.target.value)}
                      placeholder={f.placeholder}
                      className="h-9 text-sm"
                      data-testid={`post-context-${f.key}`}
                    />
                  ) : (
                    <p
                      className="text-sm text-foreground/90 min-h-[2.25rem] px-2 py-1.5 rounded border border-border/40 bg-secondary/40"
                      data-testid={`post-context-${f.key}-display`}
                    >
                      {context?.[f.key] || (
                        <span className="text-muted-foreground italic">—</span>
                      )}
                    </p>
                  )}
                </div>
              ))}
            </div>

            <div className="grid lg:grid-cols-2 gap-3">
              {TEXTAREA_FIELDS.map((f) => (
                <div key={f.key} className="space-y-1.5">
                  <Label className="text-xs font-medium">{f.label}</Label>
                  {editing ? (
                    <Textarea
                      value={draft[f.key] || ''}
                      onChange={(e) => handleField(f.key, e.target.value)}
                      placeholder={f.placeholder}
                      rows={f.rows}
                      className="text-sm resize-none"
                      data-testid={`post-context-${f.key}`}
                    />
                  ) : (
                    <p
                      className="text-sm text-foreground/90 px-2 py-1.5 rounded border border-border/40 bg-secondary/40 min-h-[3rem] whitespace-pre-wrap"
                      data-testid={`post-context-${f.key}-display`}
                    >
                      {context?.[f.key] || (
                        <span className="text-muted-foreground italic">—</span>
                      )}
                    </p>
                  )}
                </div>
              ))}
            </div>

            <div className="grid lg:grid-cols-3 gap-4 pt-1">
              {LIST_FIELDS.map((f) =>
                editing ? (
                  <ListEditor
                    key={f.key}
                    label={f.label}
                    hint={f.hint}
                    items={draft[f.key] || []}
                    onChange={(v) => handleField(f.key, v)}
                    placeholder={f.placeholder}
                    testIdBase={`post-context-${f.key}`}
                  />
                ) : (
                  <div key={f.key} className="space-y-1.5">
                    <Label className="text-xs font-medium flex items-center gap-1.5">
                      <f.icon size={12} className="text-muted-foreground" />
                      {f.label}
                    </Label>
                    {f.hint && (
                      <p className="text-[11px] text-muted-foreground">{f.hint}</p>
                    )}
                    <div className="flex flex-wrap gap-1.5 min-h-[2.25rem] px-1 py-1 rounded border border-border/40 bg-secondary/40">
                      {(context?.[f.key] || []).length === 0 ? (
                        <span className="text-xs text-muted-foreground italic px-1.5 py-1">
                          —
                        </span>
                      ) : (
                        (context[f.key] || []).map((x, i) => (
                          <Badge
                            key={`${x}-${i}`}
                            variant="secondary"
                            className="rounded-md font-normal"
                            data-testid={`post-context-${f.key}-display-${i}`}
                          >
                            {x}
                          </Badge>
                        ))
                      )}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
